<template>
  <v-dialog
    v-model="isDialogOpen"
    max-width="600px"
    :retain-focus="false"
    @keydown.esc="closeDialog"
    content-class="fixed-height-md"
  >
    <modal-card
      :title="dialogTitle"
      :closable="!saving"
      close-button
      fixed
      @close="closeDialog"
    >
      <v-fade-transition>
        <div v-if="saved" class="success-message pr-4 pl-4 text-center">
          <v-icon color="primary">check</v-icon>
          <h4 class="mb-5">
            "{{ reportTitle }}"<br />
            has been saved!
          </h4>
          <v-btn color="appMain" @click="goToSavedReports">
            View Saved Reports
          </v-btn>
        </div>
        <div v-else class="pa-4">
          <v-alert
            v-if="errorMessage"
            type="error"
            dense
            text
            dismissible
            class="mb-4"
            @input="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <v-text-field
            v-model="reportTitle"
            label="Report Name"
            class="mb-4"
          />

          <div class="mb-5">
            <v-checkbox
              v-model="portalVisible"
              label="Visible in portal"
              hide-details
              dense
              class="mt-0"
            />
          </div>

          <v-alert
            v-if="hasVisibleControls"
            type="info"
            dense
            text
            class="mb-4"
          >
            Locked filters cannot be changed by viewers when shared
          </v-alert>

          <div
            v-for="{ key, config, component } in visibleControls"
            :key="key"
            class="mb-5 save-control-row"
          >
            <div class="save-control-field">
              <component
                :is="component"
                :schema="controlSchema(config)"
                :value="shareInputValue(config)"
                @input="setShareInput(config, $event)"
              />
            </div>
            <div class="save-control-toggle">
              <v-checkbox
                v-model="lockedRules[config.param]"
                label="Locked"
                hide-details
                dense
                :disabled="config.shareable === false"
              />
            </div>
          </div>
        </div>
      </v-fade-transition>

      <template v-slot:actions>
        <v-btn
          v-if="!saved"
          color="appMain"
          :loading="saving"
          @click="handleSave"
        >
          Save
        </v-btn>
      </template>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import isEqual from 'lodash/isEqual';
import EventBus from '@/factories/eventBus';

import {
  getControlComponent,
  buildControlSchema,
  getInputValue,
  parseInputEvent,
} from './reportControlsHelper';

import ReportTrackerNames from './controls/ReportTrackerNames';
import ReportGroups from './controls/ReportGroups';
import ReportContactField from './controls/ReportContactField';
import ReportContactFilter from './controls/ReportContactFilter';
import ReportCampaign from './controls/ReportCampaign';
import ReportOptionList from './controls/ReportOptionList';
import ReportContacts from './controls/ReportContacts';
import ReportCustomObjects from './controls/ReportCustomObjects';
import ReportTimeframe from './controls/ReportTimeframe';
import ReportOfficePattern from './controls/ReportOfficePattern';
import ReportObject from './controls/ReportObject';

import DateField from '@/components/formFields/DateField';
import TextField from '@/components/formFields/TextField';
import NumberField from '@/components/formFields/NumberField';
import CheckboxField from '@/components/formFields/CheckboxField';
import UserListField from '@/components/formFields/UserListField';

export default {
  name: 'CustomReportFormDialog',
  components: {
    ReportTrackerNames,
    ReportGroups,
    ReportContactField,
    ReportContactFilter,
    ReportCampaign,
    DateField,
    TextField,
    NumberField,
    CheckboxField,
    ReportOptionList,
    ReportContacts,
    ReportCustomObjects,
    UserListField,
    ReportTimeframe,
    ReportOfficePattern,
    ReportObject,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isDialogOpen: false,
      saving: false,
      saved: false,
      errorMessage: '',
      customReportId: null,
      filters: {},
      reportTitle: '',
      initialTitle: '',
      portalVisible: false,
      shareInputs: {},
      lockedRules: {},
    };
  },

  computed: {
    ...mapGetters('customReports', ['byId']),

    customReport() {
      return this.customReportId ? this.byId(this.customReportId) : null;
    },

    isEditMode() {
      return !!this.customReport;
    },

    dialogTitle() {
      if (this.saved) return 'Report Saved!';
      return this.isEditMode ? 'Edit Report' : 'Save Report';
    },

    isDirty() {
      if (this.isEditMode) {
        const cr = this.customReport;
        if (!cr) return false;
        const input = cr.input || {};
        const savedShareInputs = {};
        const savedLockedRules = {};
        Object.entries(input).forEach(([param, settings]) => {
          if (settings.value !== null && settings.value !== undefined) {
            savedShareInputs[param] = settings.value;
          }
          savedLockedRules[param] = settings.rule === 'locked';
        });
        return (
          this.reportTitle !== (cr.title || '') ||
          this.portalVisible !== (cr.portal_visible ?? false) ||
          !isEqual(this.shareInputs, savedShareInputs) ||
          !isEqual(this.lockedRules, savedLockedRules)
        );
      }
      return (
        this.reportTitle !== this.initialTitle ||
        this.portalVisible !== false ||
        !isEqual(this.shareInputs, this.filters) ||
        Object.values(this.lockedRules).some((v) => v)
      );
    },

    visibleControls() {
      const controls = this.report?.controls || {};
      return Object.entries(controls)
        .map(([key, config]) => ({
          key,
          config,
          component: getControlComponent(key, config),
        }))
        .filter((item) => item.component);
    },

    hasVisibleControls() {
      return this.visibleControls.length > 0;
    },
  },

  mounted() {
    EventBus.$on('openCustomReportFormDialog', this.openDialog);
  },

  beforeDestroy() {
    EventBus.$off('openCustomReportFormDialog', this.openDialog);
  },

  methods: {
    ...mapActions('customReports', ['createOne', 'save']),

    openDialog({ customReportId = null, filters = {}, title = '' } = {}) {
      this.customReportId = customReportId;
      this.filters = filters;
      this.saved = false;
      this.errorMessage = '';
      this.initFormData(title);
      this.isDialogOpen = true;
    },

    initFormData(title = '') {
      if (this.isEditMode) {
        const cr = this.byId(this.customReportId);
        this.reportTitle = cr?.title || '';
        this.portalVisible = cr?.portal_visible ?? false;
        this.initFromCustomReport(cr);
      } else {
        this.initialTitle = title || this.report?.title || '';
        this.reportTitle = this.initialTitle;
        this.portalVisible = false;
        this.shareInputs = { ...this.filters };
        this.initLockedRules();
      }
    },

    closeDialog() {
      if (this.saved) {
        this.isDialogOpen = false;
        return;
      }

      if (this.isDirty) {
        this.openConfirmationModal({
          title: 'Unsaved Changes',
          text: 'You have unsaved changes. Are you sure you want to discard them?',
          buttonText: 'Discard',
          onConfirm: () => {
            this.isDialogOpen = false;
          },
        });
        return;
      }

      this.isDialogOpen = false;
    },

    initFromCustomReport(cr) {
      const input = cr?.input || {};
      const shareInputs = {};
      const lockedRules = {};

      Object.entries(input).forEach(([param, settings]) => {
        if (settings.value !== null && settings.value !== undefined) {
          shareInputs[param] = settings.value;
        }
        lockedRules[param] = settings.rule === 'locked';
      });

      this.shareInputs = shareInputs;
      this.lockedRules = lockedRules;
    },

    initLockedRules() {
      const controls = this.report?.controls || {};
      this.lockedRules = {};
      Object.values(controls).forEach((config) => {
        if (config.param) {
          this.lockedRules[config.param] = config.shareable === false;
        }
      });
    },

    shareInputValue(config) {
      return getInputValue(this.shareInputs, config);
    },

    controlSchema(config) {
      return buildControlSchema(config, { dark: false, visibleCount: 3 });
    },

    setShareInput(config, event) {
      const { param, value } = parseInputEvent(config, event);
      if (param) {
        this.$set(this.shareInputs, param, value ?? null);
      }
    },

    buildInputParams() {
      const params = {};
      const controls = this.report?.controls || {};
      Object.values(controls).forEach((config) => {
        const param = config.param;
        if (param) {
          params[param] = {
            rule: this.lockedRules[param] ? 'locked' : 'editable',
            value: this.shareInputs[param] ?? null,
          };
        }
      });
      return params;
    },

    goToSavedReports() {
      this.isDialogOpen = false;
      this.$emit('openSavedReports');
    },

    async handleSave() {
      if (!this.reportTitle.trim()) {
        this.errorMessage = 'Please enter a report name.';
        return;
      }

      this.saving = true;
      this.errorMessage = '';

      try {
        if (this.isEditMode) {
          await this.updateReport();
          this.isDialogOpen = false;
        } else {
          await this.createReport();
          this.saved = true;
        }
      } catch {
        this.errorMessage = `Failed to ${
          this.isEditMode ? 'update' : 'save'
        } report. Please try again.`;
      } finally {
        this.saving = false;
      }
    },

    async updateReport() {
      await this.save({
        model: {
          id: this.customReport.id,
          title: this.reportTitle.trim(),
          secure: true,
          portal_visible: this.portalVisible,
          input: this.buildInputParams(),
        },
      });
    },

    async createReport() {
      await this.createOne({
        title: this.reportTitle.trim(),
        class: this.report.id,
        secure: true,
        portal_visible: this.portalVisible,
        input: this.buildInputParams(),
      });
    },
  },
};
</script>

<style lang="less">
.success-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  .v-icon {
    font-size: 80px;
  }
}

.save-control-row {
  .field__inner-title {
    display: none;
  }
}

.save-control-field {
  flex: 1;
}

.save-control-toggle {
  flex-shrink: 0;
  padding-top: 4px;
}
</style>
